import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import QrCodeIcon from "@mui/icons-material/QrCode";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CustomTabs from "../../common/custom-tabs";
import StatTile from "../../common/stat-tile";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import ButtonThree from "../../common/third-button";
import TableSearchInput from "../TableSearchInput";
import TableAutocomplete from "../../common/table-autocomplete";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";
import { useAuth } from "../../hooks/AuthProvider";
import { formatWithTzLabel } from "../../utils/dateUtils";
import {
  PHARMACY_EMPLOYEES_ENDPOINT,
  PHARMACY_CUSTOMERS_ENDPOINT,
  PHARMACY_CONTACTS_ENDPOINT,
  PHARMACY_DASHBOARD_STATS_ENDPOINT,
  PHARMACY_MASTER_DATA_REPORT_ENDPOINT,
} from "../../config/urls";
import PharmacyUploadPanel from "./PharmacyUploadPanel";
import PerformanceReportPanel from "./PerformanceReportPanel";
import EmployeeQrModal from "./EmployeeQrModal";

const TABS = [
  { value: "employees", label: "Employees" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "contacts", label: "Pharmacy Contacts" },
  { value: "report", label: "Master Data Report" },
  { value: "performance", label: "Performance Report" },
  { value: "upload", label: "Upload" },
];

const employeeLabel = (e) => `${e.employee_name} (${e.employee_id})`;
// "Nikhil Sharma (emp111)" -> "Nikhil Sharma emp111": the API matches each word, and "(emp111)" matches nothing
const toSearchTerms = (text) => text.replace(/[()]/g, " ").replace(/\s+/g, " ").trim();

const TYPE_FILTER_OPTIONS = [
  { label: "All types", value: "" },
  { label: "General", value: "false" },
  { label: "Modern trade", value: "true" },
];

const ENDPOINT_BY_TAB = {
  employees: PHARMACY_EMPLOYEES_ENDPOINT,
  pharmacy: PHARMACY_CUSTOMERS_ENDPOINT,
  contacts: PHARMACY_CONTACTS_ENDPOINT,
  report: PHARMACY_MASTER_DATA_REPORT_ENDPOINT,
};

function TypePill({ mtFlag }) {
  return <AppChip label={mtFlag ? "MT" : "General"} color={mtFlag ? "info" : "default"} variant="outlined" />;
}

function phoneDisplay(row) {
  return row.phone_no ? `+${row.country_code || ""} ${row.phone_no}` : "—";
}

const EMPLOYEE_COLUMNS = [
  { id: "employee_id", label: "Employee ID" },
  { id: "employee_name", label: "Name" },
  { id: "phone", label: "Phone", render: phoneDisplay },
  { id: "designation", label: "Designation" },
  { id: "abm_name", label: "ABM Name" },
  { id: "sub_region", label: "Sub Region" },
  { id: "abm_hq", label: "ABM HQ" },
  { id: "mn_hq", label: "MN HQ" },
  { id: "mt_flag", label: "Type", render: (r) => <TypePill mtFlag={r.mt_flag} /> },
  {
    id: "is_active", label: "Active",
    render: (r) => <AppChip label={r.is_active ? "Active" : "Inactive"} color={r.is_active ? "success" : "warning"} variant="outlined" />,
  },
];

// Mirrors EMPLOYEE_COLUMNS for CSV export — same fields as the on-screen
// table (QR code column excluded, it's an action button, not data), with
// the rendered chip/pill columns flattened to plain text values.
const EMPLOYEE_CSV_COLUMNS = [
  { id: "employee_id", label: "Employee ID" },
  { id: "employee_name", label: "Name" },
  { id: "phone", label: "Phone", csvValue: (r) => (r.phone_no ? `+${r.country_code || ""} ${r.phone_no}` : "") },
  { id: "designation", label: "Designation" },
  { id: "abm_name", label: "ABM Name" },
  { id: "sub_region", label: "Sub Region" },
  { id: "abm_hq", label: "ABM HQ" },
  { id: "mn_hq", label: "MN HQ" },
  { id: "mt_flag", label: "Type", csvValue: (r) => (r.mt_flag ? "MT" : "General") },
  { id: "is_active", label: "Active", csvValue: (r) => (r.is_active ? "Active" : "Inactive") },
];

const CUSTOMER_COLUMNS = [
  { id: "gstin", label: "GSTIN", render: (r) => r.gstin || "—" },
  { id: "source", label: "Source", render: (r) => r.source || "—" },
  { id: "store_id", label: "Store ID", render: (r) => r.store_id || "—" },
  { id: "ecubix_id", label: "Ecubix ID" },
  { id: "customer_name", label: "Customer Name" },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  { id: "employee_status", label: "Employee Status" },
  {
    id: "employee", label: "Assigned Employee",
    render: (r) => (r.employee_name ? `${r.employee_name} (${r.employee_code})` : "—"),
  },
  {
    id: "mapping_status", label: "Mapping",
    render: (r) => <AppChip label={r.mapping_status_display} color={r.mapping_status === "RESOLVED" ? "success" : "warning"} variant="outlined" />,
  },
  { id: "mn_hq", label: "MN HQ" },
  { id: "mt_flag", label: "Type", render: (r) => <TypePill mtFlag={r.mt_flag} /> },
];

const CONTACT_COLUMNS = [
  { id: "first_name", label: "First Name" },
  { id: "last_name", label: "Last Name" },
  { id: "pharmacy_role", label: "Role", render: (r) => <AppChip label={r.pharmacy_role_display} color="info" variant="outlined" /> },
  { id: "phone", label: "WhatsApp Phone", render: phoneDisplay },
  { id: "whatsapp_name", label: "WhatsApp Name", render: (r) => r.whatsapp_name || "—" },
  { id: "pharmacy_name", label: "Pharmacy", render: (r) => r.pharmacy_name || r.gstin || r.store_id || "—" },
  {
    id: "status", label: "Status",
    render: (r) => (
      <AppChip
        label={r.status_display}
        color={r.status === "VERIFIED" ? "success" : r.status === "DRAFT" ? "default" : "warning"}
        variant="outlined"
      />
    ),
  },
  {
    id: "employee", label: "Mapped Employee",
    render: (r) => (r.employee_name ? `${r.employee_name} (${r.employee_code})` : "—"),
  },
  { id: "created_at", label: "Created At", render: (r) => formatWithTzLabel(r.created_at) },
];

// Mirrors CONTACT_COLUMNS for CSV export, except "Mapped Employee" is split
// into separate Name/ID columns instead of the combined on-screen display.
const CONTACT_CSV_COLUMNS = [
  { id: "first_name", label: "First Name" },
  { id: "last_name", label: "Last Name" },
  { id: "pharmacy_role_display", label: "Role" },
  { id: "country_code", label: "Country Code" },
  { id: "phone_no", label: "WhatsApp Phone" },
  { id: "whatsapp_name", label: "WhatsApp Name" },
  { id: "pharmacy_name", label: "Pharmacy", csvValue: (r) => r.pharmacy_name || r.gstin || r.store_id || "" },
  { id: "status_display", label: "Status" },
  { id: "employee_name", label: "Mapped Employee Name" },
  { id: "employee_code", label: "Mapped Employee ID" },
  { id: "created_at", label: "Created At", csvValue: (r) => formatWithTzLabel(r.created_at) },
];

// Order and labels match OTM's specified 18-column report exactly.
const REPORT_COLUMNS = [
  { id: "gstin_or_store_id", label: "GSTIN/StoreID", render: (r) => r.gstin_or_store_id || "—" },
  { id: "source", label: "Source", render: (r) => r.source || "—" },
  { id: "ecubix_code", label: "Ecubix Code", render: (r) => r.ecubix_code || "—" },
  { id: "customer_name", label: "Customer Name", render: (r) => r.customer_name || "—" },
  { id: "customer_city", label: "Customer_City", render: (r) => r.customer_city || "—" },
  { id: "customer_state", label: "Customer_State", render: (r) => r.customer_state || "—" },
  { id: "emp_id", label: "EMP ID", render: (r) => r.emp_id || "—" },
  { id: "employee_name", label: "Employee Name", render: (r) => r.employee_name || "—" },
  { id: "employee_country_code", label: "Employee_Country_Code", render: (r) => r.employee_country_code || "—" },
  { id: "employee_phone_1", label: "Employee_Phone_1", render: (r) => r.employee_phone_1 || "—" },
  { id: "abm_name", label: "ABM Name", render: (r) => r.abm_name || "—" },
  { id: "sub_region", label: "sub_region", render: (r) => r.sub_region || "—" },
  { id: "abm_hq", label: "abm_hq", render: (r) => r.abm_hq || "—" },
  { id: "mn_hq", label: "MN HQ", render: (r) => r.mn_hq || "—" },
  { id: "pharmacy_contact_first_name", label: "Pharmacy_contact_First Name" },
  { id: "pharmacy_contact_last_name", label: "Pharmacy_contact_Last Name" },
  { id: "role", label: "Role" },
  { id: "pharmacy_contact_country_code", label: "Pharmacy_contact_Country_Code", render: (r) => r.pharmacy_contact_country_code || "—" },
  { id: "pharmacy_contact_mob_number", label: "Pharmacy_contact_Mob Number", render: (r) => r.pharmacy_contact_mob_number || "—" },
  { id: "scan_date", label: "Scan_Date", render: (r) => formatWithTzLabel(r.scan_date) },
];

function downloadCsv(rows, columns, filename) {
  if (!rows.length) return;
  const header = columns.map((c) => c.label).join(",");
  const lines = rows.map((r) =>
    columns.map((c) => `"${String((c.csvValue ? c.csvValue(r) : r[c.id]) ?? "").replace(/"/g, '""')}"`).join(",")
  );
  const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function employeeColumnsWithQr(onOpenQr) {
  return [
    ...EMPLOYEE_COLUMNS,
    {
      id: "qr", label: "QR Code",
      render: (r) => (
        <ButtonThree startIcon={<QrCodeIcon fontSize="small" />} onClick={() => onOpenQr(r)} sx={{ height: 32, fontSize: 12 }}>
          {r.qr_code_url ? "View QR" : "Generate QR"}
        </ButtonThree>
      ),
    },
  ];
}

const COLUMNS_BY_TAB = {
  pharmacy: CUSTOMER_COLUMNS,
  contacts: CONTACT_COLUMNS,
  report: REPORT_COLUMNS,
};

const SEARCH_PLACEHOLDER_BY_TAB = {
  employees: "Search name or employee ID",
  pharmacy: "Search name, GSTIN, store ID, assigned employee",
  contacts: "Search name, phone, pharmacy",
  report: "Search Employee ID, Employee Name, Ecubix Code, or GSTIN/StoreID",
};

export default function PharmacyDashboard() {
  const { getValidToken } = useAuth();
  const [tab, setTab] = useState("employees");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  // Pharmacy tab: employee picked from the search box's suggestions -> show only their pharmacies.
  // The filter is derived from the box text, so it applies exactly while the box shows that employee's label.
  const [pickedEmployee, setPickedEmployee] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeOptionsLoading, setEmployeeOptionsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [qrEmployee, setQrEmployee] = useState(null);
  const [bulkQrLoading, setBulkQrLoading] = useState(false);
  const [bulkQrNote, setBulkQrNote] = useState(null);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastNote, setBroadcastNote] = useState(null);
  const [contactsExportLoading, setContactsExportLoading] = useState(false);
  const [reportExportLoading, setReportExportLoading] = useState(false);
  const [employeesExportLoading, setEmployeesExportLoading] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant };
  }, [getValidToken]);

  const fetchStats = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(PHARMACY_DASHBOARD_STATS_ENDPOINT, { headers });
      if (!res.ok) throw new Error("Failed to fetch stats");
      setStats(await res.json());
    } catch (err) {
      console.error("Error fetching OTM dashboard stats:", err);
    }
  }, [authHeaders]);

  const assignedEmployee =
    tab === "pharmacy" && pickedEmployee && search === employeeLabel(pickedEmployee) ? pickedEmployee : null;

  const fetchRows = useCallback(async () => {
    const endpoint = ENDPOINT_BY_TAB[tab];
    if (!endpoint) return;

    setLoading(true);
    const params = new URLSearchParams({ limit: rowsPerPage, offset: page * rowsPerPage });
    // A picked employee suggestion filters exactly; otherwise the box is a normal text search
    if (assignedEmployee) params.set("employee__employee_id", assignedEmployee.employee_id);
    else if (search.trim()) params.set("search", tab === "pharmacy" ? toSearchTerms(search) : search.trim());
    if (typeFilter && tab !== "contacts") params.set("mt_flag", typeFilter);

    try {
      const headers = await authHeaders();
      const res = await fetch(`${endpoint}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch data");
      const data = await res.json();
      setRows(data.results ?? []);
      setTotalCount(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching OTM data:", err);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [tab, page, rowsPerPage, search, typeFilter, assignedEmployee, authHeaders]);

  // Assigned-employee suggestions for what's typed in the Pharmacy search box (server-side, by name or ID)
  useEffect(() => {
    if (tab !== "pharmacy" || assignedEmployee || !toSearchTerms(search)) {
      setEmployeeOptions([]);
      setEmployeeOptionsLoading(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setEmployeeOptionsLoading(true);
      try {
        const params = new URLSearchParams({ limit: 10, offset: 0, search: toSearchTerms(search) });
        const headers = await authHeaders();
        const res = await fetch(`${PHARMACY_EMPLOYEES_ENDPOINT}?${params}`, { headers });
        if (!res.ok) throw new Error("Failed to fetch employees");
        const data = await res.json();
        if (!cancelled) setEmployeeOptions(data.results ?? []);
      } catch (err) {
        console.error("Error fetching OTM employees:", err);
        if (!cancelled) setEmployeeOptions([]);
      } finally {
        if (!cancelled) setEmployeeOptionsLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); setEmployeeOptionsLoading(false); };
  }, [tab, search, assignedEmployee, authHeaders]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (tab !== "upload" && tab !== "performance") fetchRows(); }, [fetchRows, tab]);

  const hasFilters = Boolean(search || typeFilter || pickedEmployee);
  const handleResetFilters = () => {
    setSearch("");
    setTypeFilter("");
    setPickedEmployee(null);
    setPage(0);
  };

  const handleTabChange = (_, value) => {
    setTab(value);
    setSearch("");
    setTypeFilter("");
    setPickedEmployee(null);
    setPage(0);
  };

  const handleUploaded = () => {
    fetchStats();
    fetchRows();
  };

  const handleQrGenerated = () => {
    fetchRows();
  };

  const handleGenerateAllQr = async () => {
    setBulkQrLoading(true);
    setBulkQrNote(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${PHARMACY_EMPLOYEES_ENDPOINT}generate-all-qr/`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ only_missing: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to generate QR codes.");
      setBulkQrNote(`Generated ${data.generated} QR code(s).`);
      fetchRows();
    } catch (err) {
      setBulkQrNote(err.message || "Failed to generate QR codes.");
    } finally {
      setBulkQrLoading(false);
    }
  };

  const handleBroadcastQr = async () => {
    setBroadcastLoading(true);
    setBroadcastNote(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${PHARMACY_EMPLOYEES_ENDPOINT}broadcast-qr/`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to start QR broadcast.");
      setBroadcastNote(data.detail || "QR broadcast started — check broadcast logs for results.");
    } catch (err) {
      setBroadcastNote(err.message || "Failed to start QR broadcast.");
    } finally {
      setBroadcastLoading(false);
    }
  };

  // Fetches every row matching the current search filter (ignoring
  // pagination) via a large limit, then builds and downloads the CSV
  // client-side — shared by every tab's Export button so each one exports
  // the full filtered result set, not just the currently-loaded page.
  const exportAllRows = async (endpoint, columns, filename, setExportLoading) => {
    setExportLoading(true);
    try {
      const headers = await authHeaders();
      const params = new URLSearchParams({ limit: 100000, offset: 0 });
      if (search.trim()) params.set("search", search.trim());
      if (typeFilter && tab !== "contacts") params.set("mt_flag", typeFilter);

      const res = await fetch(`${endpoint}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch data for export");
      const data = await res.json();
      downloadCsv(data.results ?? [], columns, filename);
    } catch (err) {
      console.error("Error exporting CSV:", err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportContacts = () =>
    exportAllRows(PHARMACY_CONTACTS_ENDPOINT, CONTACT_CSV_COLUMNS, "otm_pharmacy_contacts.csv", setContactsExportLoading);

  const handleExportReport = () =>
    exportAllRows(PHARMACY_MASTER_DATA_REPORT_ENDPOINT, REPORT_COLUMNS, "otm_master_data_report.csv", setReportExportLoading);

  const handleExportEmployees = () =>
    exportAllRows(PHARMACY_EMPLOYEES_ENDPOINT, EMPLOYEE_CSV_COLUMNS, "otm_employees.csv", setEmployeesExportLoading);

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ ...typography.h1, mb: 0.5 }}>Pharmacy Data</Typography>
      <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryGray50, mb: 3 }}>
        OTM master data — employees, pharmacies, and chatbot-captured pharmacy contacts.
      </Typography>

      {stats && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <StatTile value={stats.scans_this_week} label="Scans this week" />
          <StatTile value={stats.pharmacies_enhanced} label={`Pharmacies enhanced of ${stats.total_customers}`} />
          <StatTile value={stats.active_employees} label={`Active field employees of ${stats.total_employees}`} />
          <StatTile
            value={stats.duplicate_scans_flagged}
            label="Duplicate scans flagged"
            valueColor={stats.duplicate_scans_flagged > 0 ? "#b45309" : undefined}
          />
        </Box>
      )}

      <CustomTabs value={tab} onChange={handleTabChange} tabs={TABS} sx={{ mb: 2.5 }} />

      {tab === "upload" ? (
        <PharmacyUploadPanel onUploaded={handleUploaded} />
      ) : tab === "performance" ? (
        <PerformanceReportPanel />
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexDirection: { xs: "column", sm: "row" } }}>
            <Box sx={{ flex: 1, maxWidth: { xs: "100%", sm: 400 } }}>
              {tab === "pharmacy" ? (
                // Same search box, plus a dropdown of matching assigned employees while typing
                <TableAutocomplete
                  freeSolo
                  width="100%"
                  value={assignedEmployee}
                  inputValue={search}
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") {
                      setSearch(value);
                      setPage(0);
                    } else if (reason === "clear") {
                      setSearch("");
                      setPickedEmployee(null);
                      setPage(0);
                    }
                  }}
                  onChange={(_, value) => {
                    if (value && typeof value === "object") {
                      setPickedEmployee(value);
                      setSearch(employeeLabel(value));
                      setPage(0);
                    }
                  }}
                  options={employeeOptions}
                  loading={employeeOptionsLoading}
                  getOptionLabel={(e) => (typeof e === "string" ? e : employeeLabel(e))}
                  isOptionEqualToValue={(option, value) => option.employee_id === value.employee_id}
                  placeholder={SEARCH_PLACEHOLDER_BY_TAB[tab]}
                  tooltip={SEARCH_PLACEHOLDER_BY_TAB[tab]}
                />
              ) : (
                <TableSearchInput
                  clearable
                  searchQuery={search}
                  onSearchChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  placeholder={SEARCH_PLACEHOLDER_BY_TAB[tab]}
                  tooltip={SEARCH_PLACEHOLDER_BY_TAB[tab]}
                />
              )}
            </Box>
            {tab !== "contacts" && tab !== "report" && (
              <AppDropdown
                menuItems={TYPE_FILTER_OPTIONS}
                selectedValue={typeFilter}
                onMenuSelect={(value) => { setTypeFilter(value); setPage(0); }}
              >
                {TYPE_FILTER_OPTIONS.find((o) => o.value === typeFilter)?.label || "All types"}
              </AppDropdown>
            )}
            <ButtonThree
              startIcon={<RestartAltIcon fontSize="small" />}
              onClick={handleResetFilters}
              disabled={!hasFilters}
            >
              Reset
            </ButtonThree>
            {tab === "employees" && (
              <ButtonThree
                startIcon={<QrCodeIcon fontSize="small" />}
                onClick={handleGenerateAllQr}
                disabled={bulkQrLoading}
              >
                {bulkQrLoading ? "Generating…" : "Generate all QR"}
              </ButtonThree>
            )}
            {tab === "employees" && (
              <ButtonThree
                startIcon={<SendIcon fontSize="small" />}
                onClick={handleBroadcastQr}
                disabled={broadcastLoading}
              >
                {broadcastLoading ? "Starting…" : "Broadcast QR Code"}
              </ButtonThree>
            )}
            {tab === "employees" && (
              <ButtonThree
                startIcon={<DownloadIcon fontSize="small" />}
                onClick={handleExportEmployees}
                disabled={employeesExportLoading || totalCount === 0}
              >
                {employeesExportLoading ? "Exporting…" : "Export"}
              </ButtonThree>
            )}
            {tab === "report" && (
              <ButtonThree
                startIcon={<DownloadIcon fontSize="small" />}
                onClick={handleExportReport}
                disabled={reportExportLoading || totalCount === 0}
              >
                {reportExportLoading ? "Exporting…" : "Export"}
              </ButtonThree>
            )}
            {tab === "contacts" && (
              <ButtonThree
                startIcon={<DownloadIcon fontSize="small" />}
                onClick={handleExportContacts}
                disabled={contactsExportLoading || totalCount === 0}
              >
                {contactsExportLoading ? "Exporting…" : "Export"}
              </ButtonThree>
            )}
          </Box>

          {bulkQrNote && (
            <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50, mb: 1.5 }}>
              {bulkQrNote}
            </Typography>
          )}
          {broadcastNote && (
            <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50, mb: 1.5 }}>
              {broadcastNote}
            </Typography>
          )}

          <AppTable
            columns={tab === "employees" ? employeeColumnsWithQr(setQrEmployee) : COLUMNS_BY_TAB[tab]}
            rows={rows}
            rowKey={tab === "report" ? undefined : "id"}
            loading={loading}
            emptyMessage={`No ${tab === "pharmacy" ? "pharmacies" : tab === "report" ? "report rows" : tab} found`}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </>
      )}

      {qrEmployee && (
        <EmployeeQrModal
          employee={qrEmployee}
          onClose={() => setQrEmployee(null)}
          onGenerated={handleQrGenerated}
        />
      )}
    </Box>
  );
}
